import { Component, EventEmitter, Input, Output, ViewChild, inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { PreviewTemplateComponent } from '../../../../features/template/component/preview-template/preview-template.component';
import { LoaderService } from '../../../services/loader/loader.service';
import { TemplateService } from '../../../services/template/template-service';
import { ToastService } from '../../../services/toast/toast.service';
import { InspectionService } from '../../../services/inspection/inspection-service';
import { lastValueFrom, Subscription } from 'rxjs';
import { GlobalService } from '../../../services/global/global-service';
import { UserPreferencesService } from '../../../services/user-preferences/user-preferences.service';
import { TemplatePage } from '../../../models/template.model';

/** A template-JSON response — the template's (getTemplateJson) or a saved inspection's (getInspectionJson). */
type TemplateJsonRows = { templatejson?: TemplatePage[] | null }[] | null;

@Component({
  selector: 'app-system-form-wrapper',
  templateUrl: './system-form-wrapper.component.html',
  styleUrls: ['./system-form-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- declared in SharedModule beside PreviewTemplateComponent, which it renders and which is itself NgModule-declared; standalone migration is separate work
  standalone: false
})
export class SystemFormWrapperComponent implements OnInit, OnDestroy {
  private templateService = inject(TemplateService);
  private loaderService = inject(LoaderService);
  private globalService = inject(GlobalService);
  private userPreferencesService = inject(UserPreferencesService);
  private toastService = inject(ToastService);
  private inspectionService = inject(InspectionService);

  @Input() templateId: number;
  @Input() recordId = 0;
  /** A saved inspection to open with its answers (an edit), instead of the blank template. */
  @Input() inspectionId = 0;
  @Input() templateType = 'Inspection';   // can pass 'BootsOnGround'
  @Input() templateName = 'System Form';
  @ViewChild('templateComRef') templateComponent!: PreviewTemplateComponent;
  @Output() formReady = new EventEmitter<boolean>();
  @Output() formStatusChange = new EventEmitter<boolean>(); 
   private formSub?: Subscription;

  
  userPreferencesData: ReturnType<UserPreferencesService['getUserPreferencesData']>;

  templateJson: TemplatePage[] | null = null;

  async ngOnInit(): Promise<void> {
    this.userPreferencesData = this.userPreferencesService.getUserPreferencesData();
    if (this.templateId > 0) {
      this.loaderService.showLoader('Loading template...');
      try {
        const saved = await this.savedAnswers();
        const res: TemplateJsonRows = saved ? null : await lastValueFrom(
          this.templateService.getTemplateJson(0, this.templateId, '')
        );

        if (saved || (res && res.length > 0)) {
            this.templateJson = saved ?? res?.[0]?.templatejson ?? null;
            this.formReady.emit(true);
            setTimeout(() => {
              // The preview fills its fields from the answers only in loadTemplateData — its own init
              // builds the form from the questions and leaves them blank (COL-842). It replaces the
              // form, so before the subscription below.
              if (saved) this.templateComponent?.loadTemplateData(saved, []);
              this.subscribeToChildForm();
            });
        } else {
          this.toastService.showWarning('No template data found');
             this.formReady.emit(false);
        }
      } catch (err) {
        console.error('Error fetching template json:', err);
        this.toastService.showError('Failed to load template');
          this.formReady.emit(false);
      } finally {
        this.loaderService.hideLoader();
      }
    }
  }

  /**
   * The saved answers of `inspectionId`, as the inspection edit page loads them, or null to start from
   * the blank template: no saved inspection, none found, or it could not be read.
   */
  private async savedAnswers(): Promise<TemplatePage[] | null> {
    if (!(this.inspectionId > 0)) return null;
    try {
      const res = await lastValueFrom(this.inspectionService.getInspectionJson({
        companyId: this.userPreferencesData?.selectedCompanyId,
        id: this.inspectionId,
        templateId: this.templateId,
      }));
      const json = (res as TemplateJsonRows)?.[0]?.templatejson;
      return Array.isArray(json) && json.length ? json : null;
    } catch {
      return null;
    }
  }

  /** Expose method so parent can grab template answers */
  getTemplateDataForSave() {
    if (this.templateComponent) {
      return this.templateComponent.getTemplateDataForSave();
    }
    return [];
  }
  /** Expose templateId to parent */
  getSelectedTemplateId() {
    return this.templateId;
  }

  validateTemplateForm(): boolean {
    return this.templateComponent?.validateForm() ?? false;
  }
  markAllTouch() {
    this.templateComponent?.templatePreviewForm.markAllAsTouched();
  }
  private subscribeToChildForm() {
    const childForm = this.templateComponent?.templatePreviewForm;
    if (childForm) {
      // Emit initial state
      this.formStatusChange.emit(childForm.valid);

      this.formSub = childForm.statusChanges.subscribe(() => {
        this.formStatusChange.emit(childForm.valid);
      });
    }
  }
  ngOnDestroy() {
    this.formSub?.unsubscribe();
  }
}